'use client';

import { logError } from '@/domain/error/api';
import { type ErrorData } from '@/domain/error/types';
import { ApiError } from '@/services/ApiError';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { useEffect } from 'react';
import StackTrace from 'stacktrace-js';

const CHUNK_RELOAD_KEY = 'chunk_error_reload';
const CHUNK_RELOAD_MAX_RETRIES = 1;

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to load chunk')
  );
}

function formatStackFrames(frames: StackTrace.StackFrame[]): string {
  return frames
    .map((frame) => {
      const file = frame.fileName ?? 'unknown';
      const line = frame.lineNumber ?? '?';
      const col = frame.columnNumber ?? '?';
      const fn = frame.functionName ?? 'anonymous';
      return `  at ${fn} (${file}:${line}:${col})`;
    })
    .join('\n');
}

export default function ErrorPage({
  error,
  reset,
}: Readonly<{ error: Error & { digest?: string }; reset: () => void }>) {
  const isChunkError = isChunkLoadError(error);
  const shouldReload =
    isChunkError && Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0') < CHUNK_RELOAD_MAX_RETRIES;

  useEffect(() => {
    if (isChunkError) {
      if (shouldReload) {
        const reloadCount = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0');
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(reloadCount + 1));
        globalThis.location.reload();
      } else {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      }
      return;
    }

    if (error instanceof ApiError) {
      return;
    }

    const reportError = async () => {
      let resolvedStack = '';
      try {
        const frames = await StackTrace.fromError(error);
        resolvedStack = formatStackFrames(frames);
      } catch {
        resolvedStack = error.stack ?? '';
      }

      const errorData: ErrorData = {
        error: error.message,
        componentStack: error.stack ?? '',
        resolvedStack,
        userAgent: globalThis.navigator.userAgent,
        screen: globalThis.location.pathname,
        url: globalThis.location.href,
        timestamp: new Date().toISOString(),
        digest: error.digest,
      };

      await logError(errorData);
    };

    void reportError();
  }, [error, isChunkError, shouldReload]);

  if (shouldReload) {
    return null;
  }

  const isDev = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_APP_ENV === 'staging';

  return (
    <Box sx={{ m: 2 }}>
      <Typography component="h2" sx={{ color: 'rgb(232, 59, 70)', fontSize: '2em' }} data-testid="error-heading">
        An error occurred
      </Typography>
      <Typography component="p" sx={{ mt: 2, mb: 3 }}>
        The error has been reported to our support team. We will take a look at it and resolve the issue as soon as
        possible.
      </Typography>
      {isDev && (
        <Typography component="pre"
          sx={{ mt: 2, padding: 2, background: '#f5f5f5', overflow: 'auto', fontSize: '0.85em' }}
        >
          {error.message}
          {'\n'}
          {error.stack}
        </Typography>
      )}
      <Button onClick={reset} variant="outlined">
        Refresh
      </Button>
    </Box>
  );
}
